'use client'

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { RefObject } from 'react'
import { useRum } from '@/provider/RumProvider'
import {
  clearPerformanceStages,
  collectPerformanceStages,
  HOME_PERFORMANCE_MARKS,
  markPerformanceStage,
  recordFirstScreenReady,
} from '@/utils/firstScreenPerformance'
import { collectJsResourceCacheRumFields } from '@/utils/jsResourceCacheMetrics'
import { useListPagePerformanceLifecycle } from '@/hooks/home/useListPagePerformanceLifecycle'
import {
  collectHomeMediaReadinessRumFields,
  type HomeFirstScreenMediaReadyRecord,
  type HomeFirstScreenMediaUnit,
} from '@/utils/homeFirstScreenRumMetrics'
import {
  collectDocumentLoadRumFields,
  collectNextJsLoadRumFields,
} from '@/utils/homeColdStartPerformance'

export type { HomeFirstScreenMediaUnit } from '@/utils/homeFirstScreenRumMetrics'

export const HOME_INITIAL_PAGE_COUNT = 1
export const HOME_FIRST_SCREEN_MEDIA_TIMEOUT_MS = 60000
export const HOME_FIRST_SCREEN_MEDIA_EVENT_NAME =
  'home_first_visible_media_loaded'
const HOME_FIRST_SCREEN_EMPTY_SAMPLE_MAX_FRAMES = 60
const HOME_FIRST_SCREEN_SCROLL_TOLERANCE_PX = 2

type UseHomeFirstScreenRumOptions = {
  enabled: boolean
  dataReady: boolean
  isLoading: boolean
  mediaUnits: HomeFirstScreenMediaUnit[]
  scrollContainerRef?: RefObject<HTMLElement | null> | null
  data?: Record<string, unknown>
  timeoutMs?: number
}

type ReportRegistryEntry = {
  refCount: number
  reported: boolean
}

type TargetLockMeta = {
  targetLockFailed: boolean
  userScrolledBeforeTargetLock: boolean
  targetLockDurationMs?: number
}

const reportRegistry = new Map<string, ReportRegistryEntry>()

const acquireReportEntry = (key: string) => {
  const entry = reportRegistry.get(key) ?? {
    refCount: 0,
    reported: false,
  }
  entry.refCount += 1
  reportRegistry.set(key, entry)
  return entry
}

const releaseReportEntry = (key: string) => {
  const entry = reportRegistry.get(key)
  if (!entry) return

  entry.refCount -= 1
  if (entry.refCount <= 0) {
    reportRegistry.delete(key)
  }
}

const setToSignature = (values: string[]) => values.join('|')

const createDefaultTargetLockMeta = (): TargetLockMeta => ({
  targetLockFailed: false,
  userScrolledBeforeTargetLock: false,
})

const isAppendOnlyMediaChange = (
  previousSignature: string,
  nextUnits: HomeFirstScreenMediaUnit[]
) => {
  if (!previousSignature) return false

  const previousKeys = previousSignature.split('|')
  if (nextUnits.length < previousKeys.length) return false

  return previousKeys.every((key, index) => nextUnits[index]?.key === key)
}

const getViewportRect = (
  scrollContainerRef?: RefObject<HTMLElement | null> | null
) => {
  const container = scrollContainerRef?.current
  if (container) return container.getBoundingClientRect()

  return {
    top: 0,
    left: 0,
    right: window.innerWidth,
    bottom: window.innerHeight,
  }
}

const getScrollOffset = (
  scrollContainerRef?: RefObject<HTMLElement | null> | null
) => {
  const container = scrollContainerRef?.current
  if (container) return container.scrollTop

  return window.scrollY || window.pageYOffset || 0
}

const isElementVisibleInRect = (
  elementRect: DOMRect,
  viewportRect: Pick<DOMRect, 'top' | 'right' | 'bottom' | 'left'>
) =>
  elementRect.width > 0 &&
  elementRect.height > 0 &&
  elementRect.bottom > viewportRect.top &&
  elementRect.top < viewportRect.bottom &&
  elementRect.right > viewportRect.left &&
  elementRect.left < viewportRect.right

const collectHomePerformanceRumFields = (routeDurationMs?: number) => {
  const stages = collectPerformanceStages(
    HOME_PERFORMANCE_MARKS,
    routeDurationMs
  )

  // ClientInit 的模块 mark 只会落在首个 document route 内；热路由时该 mark 早于
  // route start，不附带 document / Next JS 时序，避免把着陆页资源误算给后续 Home。
  if (typeof stages.client_entry_started_since_route_ms !== 'number') {
    return stages
  }

  const homeMountedAt = stages.home_mounted_since_route_ms

  return {
    ...collectDocumentLoadRumFields(),
    ...collectNextJsLoadRumFields(
      undefined,
      undefined,
      typeof homeMountedAt === 'number' ? homeMountedAt : routeDurationMs
    ),
    ...stages,
  }
}

// 首页事件接近 QuickTracking 80 属性上限；以下 4 项均可由保留字段推导，首页不重复写：
// resource_scope 恒为 next_static_js，total = request + unknown，target_hit_rate 恒为 0.99，
// target_met 可由 hit_rate 判断。
const collectHomeJsResourceCacheRumFields = () => {
  const fields = collectJsResourceCacheRumFields()
  return {
    js_resource_cache_metric_version: fields.js_resource_cache_metric_version,
    js_resource_cache_request_count: fields.js_resource_cache_request_count,
    js_resource_cache_hit_count: fields.js_resource_cache_hit_count,
    js_resource_cache_miss_count: fields.js_resource_cache_miss_count,
    js_resource_cache_unknown_count: fields.js_resource_cache_unknown_count,
    js_resource_cache_hit_rate: fields.js_resource_cache_hit_rate,
    js_resource_cache_sw_controlled: fields.js_resource_cache_sw_controlled,
  }
}

export default function useHomeFirstScreenRum({
  enabled,
  dataReady,
  isLoading,
  mediaUnits,
  scrollContainerRef,
  data,
  timeoutMs = HOME_FIRST_SCREEN_MEDIA_TIMEOUT_MS,
}: UseHomeFirstScreenRumOptions) {
  const rum = useRum()
  const pageLifecycle = useListPagePerformanceLifecycle()
  const reportedRef = useRef(false)
  const reportEntryRef = useRef<ReportRegistryEntry | null>(null)
  const targetLockMetaRef = useRef<TargetLockMeta>(
    createDefaultTargetLockMeta()
  )
  const targetLockScrollOffsetRef = useRef(0)
  const targetMediaSignatureRef = useRef('')
  const targetScrollOffsetRef = useRef(0)
  const mediaElementMapRef = useRef(new Map<string, HTMLElement>())
  const mediaReadyRecordsRef = useRef(
    new Map<string, HomeFirstScreenMediaReadyRecord>()
  )
  const mediaReadySequenceRef = useRef(0)
  const [readyKeys, setReadyKeys] = useState<Set<string>>(() => new Set())
  const [targetUnits, setTargetUnits] = useState<
    HomeFirstScreenMediaUnit[] | null
  >(null)
  const [timedOut, setTimedOut] = useState(false)

  useLayoutEffect(() => {
    if (!enabled) return
    markPerformanceStage(HOME_PERFORMANCE_MARKS.homeMounted)
  }, [enabled])

  const mediaSignature = useMemo(
    () => setToSignature(mediaUnits.map((unit) => unit.key)),
    [mediaUnits]
  )

  const targetKeys = useMemo(
    () => targetUnits?.map((unit) => unit.key) ?? [],
    [targetUnits]
  )
  const targetSignature = useMemo(
    () => setToSignature(targetKeys),
    [targetKeys]
  )
  const readyTargetCount = useMemo(
    () => targetUnits?.filter((unit) => readyKeys.has(unit.key)).length ?? 0,
    [readyKeys, targetUnits]
  )
  const allTargetMediaReady = targetUnits
    ? readyTargetCount === targetUnits.length
    : false

  const hasReported = useCallback(() => {
    return reportedRef.current || reportEntryRef.current?.reported === true
  }, [])

  const markReported = useCallback(() => {
    reportedRef.current = true
    if (reportEntryRef.current) {
      reportEntryRef.current.reported = true
    }
    mediaReadyRecordsRef.current.clear()
    mediaReadySequenceRef.current = 0
  }, [])

  const getTargetLockDurationMs = useCallback(
    () => rum?.getCurrentRouteDurationMs(),
    [rum]
  )

  const getVisibleMediaUnits = useCallback(() => {
    const viewportRect = getViewportRect(scrollContainerRef)
    return mediaUnits.filter((unit) => {
      const element = mediaElementMapRef.current.get(unit.key)
      if (!element) return false
      return isElementVisibleInRect(
        element.getBoundingClientRect(),
        viewportRect
      )
    })
  }, [mediaUnits, scrollContainerRef])

  const getRegisteredMediaUnitCount = useCallback(
    () =>
      mediaUnits.filter((unit) => mediaElementMapRef.current.has(unit.key))
        .length,
    [mediaUnits]
  )

  const setTargetLockMeta = useCallback((meta: Partial<TargetLockMeta>) => {
    targetLockMetaRef.current = {
      ...targetLockMetaRef.current,
      ...meta,
    }
  }, [])

  const hasScrolledSinceTargetLockStarted = useCallback(() => {
    return (
      Math.abs(
        getScrollOffset(scrollContainerRef) - targetLockScrollOffsetRef.current
      ) > HOME_FIRST_SCREEN_SCROLL_TOLERANCE_PX
    )
  }, [scrollContainerRef])

  const lockTargetUnits = useCallback(
    (
      units: HomeFirstScreenMediaUnit[],
      userScrolledBeforeTargetLock = false
    ) => {
      setTargetLockMeta({
        targetLockFailed: false,
        userScrolledBeforeTargetLock:
          targetLockMetaRef.current.userScrolledBeforeTargetLock ||
          userScrolledBeforeTargetLock,
        targetLockDurationMs: getTargetLockDurationMs(),
      })
      targetMediaSignatureRef.current = mediaSignature
      targetScrollOffsetRef.current = getScrollOffset(scrollContainerRef)
      markPerformanceStage(HOME_PERFORMANCE_MARKS.targetLocked)
      setTargetUnits(units)
    },
    [
      getTargetLockDurationMs,
      mediaSignature,
      scrollContainerRef,
      setTargetLockMeta,
    ]
  )

  useEffect(() => {
    if (!enabled || !dataReady || isLoading) return
    markPerformanceStage(HOME_PERFORMANCE_MARKS.dataReady)

    let secondFrame = 0
    const firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(() => {
        markPerformanceStage(HOME_PERFORMANCE_MARKS.gridCommitted)
        pageLifecycle?.markVisible()
      })
    })
    return () => {
      cancelAnimationFrame(firstFrame)
      cancelAnimationFrame(secondFrame)
    }
  }, [dataReady, enabled, isLoading, pageLifecycle])

  const reportTargetLockFailure = useCallback(
    (userScrolledBeforeTargetLock: boolean) => {
      if (hasReported()) return

      const targetLockDurationMs = getTargetLockDurationMs()
      const eventData = { ...data }
      delete eventData.name

      targetLockMetaRef.current = {
        targetLockFailed: true,
        userScrolledBeforeTargetLock,
        targetLockDurationMs,
      }

      rum?.markFirstScreen(HOME_FIRST_SCREEN_MEDIA_EVENT_NAME, {
        ...eventData,
        target_scope: 'visible',
        target_lock_failed: true,
        user_scrolled_before_target_lock: userScrolledBeforeTargetLock,
        target_lock_duration_ms: targetLockDurationMs,
        target_media_count: 0,
        ready_media_count: 0,
        timed_out: false,
        timed_out_media_count: 0,
        card_count: 0,
        banner_count: 0,
        ...collectHomeMediaReadinessRumFields([], mediaReadyRecordsRef.current),
        ...collectHomeJsResourceCacheRumFields(),
        ...collectHomePerformanceRumFields(targetLockDurationMs),
      })
      pageLifecycle?.markInteractive()
      clearPerformanceStages(HOME_PERFORMANCE_MARKS)
      markReported()
    },
    [
      data,
      getTargetLockDurationMs,
      hasReported,
      markReported,
      pageLifecycle,
      rum,
    ]
  )

  useEffect(() => {
    const entry = acquireReportEntry(HOME_FIRST_SCREEN_MEDIA_EVENT_NAME)
    reportEntryRef.current = entry
    reportedRef.current = entry.reported

    return () => {
      releaseReportEntry(HOME_FIRST_SCREEN_MEDIA_EVENT_NAME)
      if (reportEntryRef.current === entry) {
        reportEntryRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    if (
      !enabled ||
      isLoading ||
      hasReported() ||
      targetUnits ||
      mediaUnits.length === 0
    ) {
      return
    }

    targetLockScrollOffsetRef.current = getScrollOffset(scrollContainerRef)
    targetLockMetaRef.current = createDefaultTargetLockMeta()
  }, [
    enabled,
    hasReported,
    isLoading,
    mediaUnits.length,
    scrollContainerRef,
    targetUnits,
  ])

  useEffect(() => {
    if (!targetUnits || hasReported()) return

    const currentMediaKeySet = new Set(mediaUnits.map((unit) => unit.key))
    const targetStillExists = targetUnits.every((unit) =>
      currentMediaKeySet.has(unit.key)
    )
    const mediaChanged = targetMediaSignatureRef.current !== mediaSignature
    const scrollOffsetChanged =
      Math.abs(
        getScrollOffset(scrollContainerRef) - targetScrollOffsetRef.current
      ) > HOME_FIRST_SCREEN_SCROLL_TOLERANCE_PX

    const appendOnlyMediaChange =
      mediaChanged &&
      isAppendOnlyMediaChange(targetMediaSignatureRef.current, mediaUnits)

    if (
      !targetStillExists ||
      (mediaChanged && !appendOnlyMediaChange && !scrollOffsetChanged)
    ) {
      setTargetUnits(null)
      setTimedOut(false)
      targetLockScrollOffsetRef.current = getScrollOffset(scrollContainerRef)
      targetLockMetaRef.current = createDefaultTargetLockMeta()
      targetMediaSignatureRef.current = ''
    }
  }, [hasReported, mediaSignature, mediaUnits, scrollContainerRef, targetUnits])

  useEffect(() => {
    if (
      !enabled ||
      isLoading ||
      hasReported() ||
      targetUnits ||
      mediaUnits.length === 0
    ) {
      return
    }

    const scrollTarget = scrollContainerRef?.current ?? window
    const handleScrollBeforeTargetLock = () => {
      if (hasReported() || targetUnits) return

      const visibleUnits = getVisibleMediaUnits()
      if (visibleUnits.length > 0) {
        lockTargetUnits(visibleUnits, true)
        return
      }

      setTargetLockMeta({
        userScrolledBeforeTargetLock: true,
      })
      if (getRegisteredMediaUnitCount() === 0) return

      reportTargetLockFailure(true)
    }

    scrollTarget.addEventListener('scroll', handleScrollBeforeTargetLock, {
      passive: true,
    })

    return () => {
      scrollTarget.removeEventListener('scroll', handleScrollBeforeTargetLock)
    }
  }, [
    enabled,
    getVisibleMediaUnits,
    getRegisteredMediaUnitCount,
    hasReported,
    isLoading,
    lockTargetUnits,
    mediaUnits.length,
    reportTargetLockFailure,
    scrollContainerRef,
    setTargetLockMeta,
    targetUnits,
  ])

  useEffect(() => {
    if (
      !enabled ||
      isLoading ||
      hasReported() ||
      targetUnits ||
      mediaUnits.length === 0
    ) {
      return
    }

    let frame = 0
    let attempts = 0

    const sampleVisibleUnits = () => {
      const visibleUnits = getVisibleMediaUnits()

      if (visibleUnits.length === 0) {
        attempts += 1
        if (attempts < HOME_FIRST_SCREEN_EMPTY_SAMPLE_MAX_FRAMES) {
          frame = requestAnimationFrame(sampleVisibleUnits)
          return
        }

        const userScrolledBeforeTargetLock =
          targetLockMetaRef.current.userScrolledBeforeTargetLock ||
          hasScrolledSinceTargetLockStarted()

        if (userScrolledBeforeTargetLock) {
          setTargetLockMeta({
            userScrolledBeforeTargetLock: true,
          })
        }

        if (userScrolledBeforeTargetLock && getRegisteredMediaUnitCount() > 0) {
          reportTargetLockFailure(true)
        }
        return
      }

      lockTargetUnits(visibleUnits)
    }

    frame = requestAnimationFrame(sampleVisibleUnits)

    return () => {
      cancelAnimationFrame(frame)
    }
  }, [
    enabled,
    hasReported,
    getVisibleMediaUnits,
    getRegisteredMediaUnitCount,
    hasScrolledSinceTargetLockStarted,
    isLoading,
    lockTargetUnits,
    mediaUnits,
    reportTargetLockFailure,
    setTargetLockMeta,
    targetUnits,
  ])

  useEffect(() => {
    setTimedOut(false)
  }, [targetSignature])

  const registerMediaElement = useCallback(
    (key: string) => (element: HTMLElement | null) => {
      if (element) {
        mediaElementMapRef.current.set(key, element)
      } else {
        mediaElementMapRef.current.delete(key)
      }
    },
    []
  )

  const markMediaReady = useCallback(
    (key: string) => {
      if (hasReported()) return

      mediaReadySequenceRef.current = recordFirstScreenReady(
        mediaReadyRecordsRef.current,
        key,
        mediaReadySequenceRef.current,
        () => rum?.getCurrentRouteDurationMs()
      )

      setReadyKeys((prev) => {
        if (prev.has(key)) return prev
        const next = new Set(prev)
        next.add(key)
        return next
      })
    },
    [hasReported, rum]
  )

  useEffect(() => {
    if (!enabled || isLoading || hasReported() || !targetUnits) {
      return
    }

    if (allTargetMediaReady) return

    const timer = window.setTimeout(() => {
      setTimedOut(true)
    }, timeoutMs)

    return () => {
      window.clearTimeout(timer)
    }
  }, [
    allTargetMediaReady,
    enabled,
    hasReported,
    isLoading,
    targetSignature,
    targetUnits,
    timeoutMs,
  ])

  useEffect(() => {
    if (!enabled || isLoading || hasReported() || !targetUnits) {
      return
    }

    if (!allTargetMediaReady && !timedOut) return

    if (allTargetMediaReady) {
      // 这里表示 React 已观察到全部目标 ready；最终上报仍等待下面两次 RAF。
      markPerformanceStage(HOME_PERFORMANCE_MARKS.allMediaReady)
    }

    let secondFrame = 0
    const firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(() => {
        if (hasReported()) return

        if (allTargetMediaReady) {
          markPerformanceStage(HOME_PERFORMANCE_MARKS.postMediaPaint)
        }

        const bannerCount = targetUnits.filter(
          (unit) => unit.type === 'banner'
        ).length
        const eventData = { ...data }
        delete eventData.name
        const targetLockMeta = targetLockMetaRef.current
        rum?.markFirstScreen(HOME_FIRST_SCREEN_MEDIA_EVENT_NAME, {
          ...eventData,
          target_scope: 'visible',
          target_lock_failed: targetLockMeta.targetLockFailed,
          user_scrolled_before_target_lock:
            targetLockMeta.userScrolledBeforeTargetLock,
          target_lock_duration_ms: targetLockMeta.targetLockDurationMs,
          target_media_count: targetUnits.length,
          ready_media_count: readyTargetCount,
          timed_out: timedOut,
          timed_out_media_count: targetUnits.length - readyTargetCount,
          card_count: targetUnits.length - bannerCount,
          banner_count: bannerCount,
          ...collectHomeMediaReadinessRumFields(
            targetUnits,
            mediaReadyRecordsRef.current
          ),
          ...collectHomeJsResourceCacheRumFields(),
          ...collectHomePerformanceRumFields(rum?.getCurrentRouteDurationMs()),
        })
        pageLifecycle?.markInteractive()
        clearPerformanceStages(HOME_PERFORMANCE_MARKS)
        markReported()
      })
    })

    return () => {
      cancelAnimationFrame(firstFrame)
      cancelAnimationFrame(secondFrame)
    }
  }, [
    data,
    enabled,
    hasReported,
    isLoading,
    allTargetMediaReady,
    markReported,
    pageLifecycle,
    readyTargetCount,
    rum,
    targetSignature,
    targetUnits,
    timedOut,
  ])

  return {
    markMediaReady,
    registerMediaElement,
  }
}
